import { Dispatch, SetStateAction } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CompanyDto } from 'tdc-web-backend/companies/schemas';
import { EnumIndustry, mapIndustry } from 'tdc-web-backend/enums/enums';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../../../../../../../../../../components/confirm-modal/ConfirmModal';
import { useRefresh } from '../../../../../../../../../../utils/hooks/crud-hooks/useRefresh';
import { primaryLight } from '../../../../../../../../../../utils/color';
import { ReactComponent as SuitcaseCheckIcon } from '../../../../../../../../../../assets/icons/layout-icons/SuitcaseCheckIcon.svg';
import CustomInput from '../../../../../../../../../../components/custom-input/CustomInput';
import useUserCompany from '../../../../../../../../../../utils/hooks/useUserCompany';

interface CompanyTargetIndustriesEditModalProps {
  isEditModalOpen: boolean;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
}

const CompanyTargetIndustriesEditModal = ({
  isEditModalOpen,
  setIsEditModalOpen,
}: CompanyTargetIndustriesEditModalProps) => {
  const { company: companyData, updateCompany, isLoadingUpdateCompany } = useUserCompany();
  const refresh = useRefresh();
  const { t } = useTranslation();
  const methods = useForm<Pick<CompanyDto, 'targetIndustries'>>({
    mode: 'onChange',
    defaultValues: {
      targetIndustries: companyData?.targetIndustries ?? ([] as EnumIndustry[]),
    },
  });

  const onSubmit = (data: Pick<CompanyDto, 'targetIndustries'>) => {
    updateCompany(data, {
      onSuccess: () => {
        refresh();
        setIsEditModalOpen(false);
      },
    });
  };

  return (
    <ConfirmModal
      width="40%"
      title={t('settings.companyProfile.targetIndustries')}
      isModalOpen={isEditModalOpen}
      setIsModalOpen={setIsEditModalOpen}
      onCancel={() => setIsEditModalOpen(false)}
      onConfirm={methods.handleSubmit(onSubmit)}
      confirmButtonText={t('common.save')}
      automaticallyCloseModalOnButtonClick={false}
      isLoading={isLoadingUpdateCompany}
      iconProps={{
        icon: <SuitcaseCheckIcon fill={primaryLight[500]} />,
        wrapperBackgroundColor: primaryLight[100],
      }}
      message={
        <FormProvider {...methods}>
          <Box component="form">
            <CustomInput
              required
              input={<CustomInput.Autocomplete />}
              choices={mapIndustry}
              translation={{ ns: 'enum' }}
              name="targetIndustries"
              label={t('settings.companyProfile.targetIndustries')}
              multiple
              placeholder={t('common.select')}
              sx={{
                width: '100%',
              }}
            />
          </Box>
        </FormProvider>
      }
    />
  );
};

export default CompanyTargetIndustriesEditModal;
